import type { ObservabilityQueryFilter } from '@scatterin/api-types';

import type { AnalyticsUrlState } from '@/lib/urlState';
import { serializeDate } from '@/lib/urlState';

const cleanList = (values: string[]): string[] | undefined => {
  const filtered = values.map((value) => value.trim()).filter(Boolean);
  return filtered.length > 0 ? filtered : undefined;
};

const maxLogWindowMs = 24 * 60 * 60 * 1000;

export const buildLogLookupWindow = (state: AnalyticsUrlState): { endTime: string; startTime: string; wasClamped: boolean } => {
  const earliestStart = new Date(state.end.getTime() - maxLogWindowMs);
  const start = state.start < earliestStart ? earliestStart : state.start;
  return {
    endTime: serializeDate(state.end),
    startTime: serializeDate(start),
    wasClamped: start.getTime() !== state.start.getTime(),
  };
};

export const buildObservabilityFilter = (state: AnalyticsUrlState): ObservabilityQueryFilter => ({
  analysisTypes: cleanList(state.analysisTypes),
  environment: state.env,
  runtimeTypes: state.runtimeTypes.length > 0 ? state.runtimeTypes : undefined,
  serviceNames: cleanList(state.serviceNames),
  sources: cleanList(state.sources),
  window: {
    endTime: serializeDate(state.end),
    startTime: serializeDate(state.start),
  },
});

export const buildEndpointScope = (state: AnalyticsUrlState) => ({
  analysisTypes: cleanList(state.analysisTypes) ?? [],
  compareDimension: state.compareDimension,
  compareMetric: state.compareMetric,
  endTime: serializeDate(state.end),
  environment: state.env,
  failureId: state.failureId ?? '',
  logGroup: state.logGroup ?? '',
  logNextToken: state.logNextToken ?? '',
  logStream: state.logStream ?? '',
  queueLimit: state.queueLimit,
  queueOffset: state.queueOffset,
  queueView: state.queueView,
  rawFactLimit: state.rawFactLimit,
  runOffset: state.runOffset,
  runPageSize: state.runPageSize,
  runId: state.runId ?? '',
  runStatuses: state.runStatuses,
  search: state.search,
  runtimeTypes: state.runtimeTypes,
  serviceNames: cleanList(state.serviceNames) ?? [],
  sessionId: state.sessionId ?? '',
  sessionBrowsers: cleanList(state.sessionBrowsers) ?? [],
  sessionDevices: cleanList(state.sessionDevices) ?? [],
  sessionErrorStatus: state.sessionErrorStatus,
  sessionOffset: state.sessionOffset,
  sessionPageSize: state.sessionPageSize,
  sessionRoutes: cleanList(state.sessionRoutes) ?? [],
  sessionVitalNames: state.sessionVitalNames,
  sources: cleanList(state.sources) ?? [],
  startTime: serializeDate(state.start),
  topLimit: state.topLimit,
  userPageToken: state.userPageToken ?? '',
  workerImageVersions: cleanList(state.workerImageVersions) ?? [],
  workloadSizeClasses: state.workloadSizeClasses,
});
